import React, { useEffect, useState } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Header from './components/Header.jsx'
import Hero from './components/Hero.jsx'
import Section from './components/Section.jsx'
import Footer from './components/Footer.jsx'
import defaultContent from './content.json'

const CMS_ENDPOINT = import.meta.env.VITE_CMS_ENDPOINT || ''

/**
 * Fetch the content blob for a given page id from KVSA Admin CMS.
 * Falls back to bundled content.json on error.
 *
 * Phase 19-CMS-MULTIPAGE (2026-05-18): now page-aware. Each page
 * has its own DDB row; home's row holds the global nav/footer.
 */
function useSiteContent(pageId) {
    const [content, setContent] = useState(defaultContent)
    const [source, setSource] = useState('bundled')
    useEffect(() => {
        if (!CMS_ENDPOINT) return
        const ac = new AbortController()
        fetch(`${CMS_ENDPOINT}?page=${encodeURIComponent(pageId)}`, { signal: ac.signal })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('cms fetch failed'))))
            .then((data) => {
                const cmsContent = (data && typeof data === 'object' && data.content) ? data.content : null
                if (cmsContent) {
                    setContent({
                        site: { ...defaultContent.site, ...(cmsContent.site || {}) },
                        nav: (cmsContent.nav && cmsContent.nav.length) ? cmsContent.nav : defaultContent.nav,
                        hero: { ...defaultContent.hero, ...(cmsContent.hero || {}) },
                        sections: (cmsContent.sections && cmsContent.sections.length) ? cmsContent.sections : defaultContent.sections,
                        footer: {
                            ...defaultContent.footer,
                            ...(cmsContent.footer || {}),
                            contact: { ...defaultContent.footer.contact, ...((cmsContent.footer || {}).contact || {}) },
                        },
                    })
                    setSource(data.source === 'db' ? 'cms' : 'default')
                }
            })
            .catch(() => { /* keep defaults */ })
        return () => ac.abort()
    }, [pageId])
    return { content, source }
}

/**
 * Phase 19-CMS-INLINE + DRAWER (2026-05-18): click-to-edit overlay
 * with bounding rect capture. Activated when URL has ?edit=1.
 */
function useCmsEditMode() {
    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (params.get('edit') !== '1') return
        const styleId = '__cms_edit_styles__'
        if (!document.getElementById(styleId)) {
            const s = document.createElement('style')
            s.id = styleId
            s.textContent = `
                [data-cms-field] { cursor: pointer !important; transition: outline 120ms ease; }
                [data-cms-field]:hover { outline: 2px dashed #f59e0b; outline-offset: 4px; background-color: rgba(251, 191, 36, 0.08); }
                [data-cms-field].__cms_active { outline: 3px solid #ea580c !important; outline-offset: 4px; }
                .__cms_edit_banner { position: fixed; top: 8px; left: 50%; transform: translateX(-50%); background: #1f2937; color: white; padding: 6px 16px; border-radius: 999px; font-size: 12px; z-index: 9999; box-shadow: 0 4px 12px rgba(0,0,0,0.2); pointer-events: none; }
            `
            document.head.appendChild(s)
        }
        const bannerId = '__cms_edit_banner__'
        if (!document.getElementById(bannerId)) {
            const b = document.createElement('div')
            b.id = bannerId
            b.className = '__cms_edit_banner'
            b.textContent = '편집 모드 - 요소를 클릭하면 옆에 편집창이 뜹니다'
            document.body.appendChild(b)
        }
        const handleClick = (e) => {
            const el = e.target.closest('[data-cms-field]')
            if (!el) return
            e.preventDefault()
            e.stopPropagation()
            const field = el.getAttribute('data-cms-field')
            document.querySelectorAll('[data-cms-field].__cms_active').forEach((n) => n.classList.remove('__cms_active'))
            el.classList.add('__cms_active')
            const r = el.getBoundingClientRect()
            const rect = {
                top: r.top, left: r.left, width: r.width, height: r.height,
                bottom: r.bottom, right: r.right,
                viewportHeight: window.innerHeight,
                viewportWidth: window.innerWidth,
            }
            // Page id: derive from current pathname (e.g. /about -> about)
            const path = window.location.pathname || '/'
            // strip the leading /KVSA-Admin/koreavsa/ basename if present
            const pageId = (() => {
                const base = '/'
                let p = path
                if (p.startsWith(base)) p = '/' + p.slice(base.length)
                p = p.replace(/^\/+/, '').replace(/\/$/, '')
                return p || 'home'
            })()
            try {
                if (window.parent && window.parent !== window) {
                    window.parent.postMessage({ type: 'cms-edit', field, page: pageId, rect }, '*')
                }
            } catch (_) { /* sandboxed */ }
        }
        document.addEventListener('click', handleClick, true)
        return () => {
            document.removeEventListener('click', handleClick, true)
            const b = document.getElementById(bannerId)
            if (b) b.remove()
        }
    }, [])
}

/**
 * Generic page renderer using the same Hero + Section schema as home.
 * Used by all routes (home / about / membership / news / contact).
 */
function PageView({ content }) {
    const sections = content?.sections || []
    return (
        <>
            <Hero content={content} />
            {sections.length > 0 && (
                <div className="bg-slate-50 py-20 md:py-28">
                    <div className="container-fluid">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {sections.map((s, i) => (
                                <Section key={s.id || i} section={s} index={i} />
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

/**
 * Wrapper that loads the right page id from CMS and renders PageView.
 */
function CmsPage({ pageId, fallbackHeader }) {
    const { content } = useSiteContent(pageId)
    // If we got real data, use it; otherwise show the page-name placeholder
    // hero so the operator can tell something is mounted.
    const hasContent = (content?.hero?.headline || '') || (content?.sections?.length || 0) > 0
    if (!hasContent) {
        return (
            <section className="container-fluid py-24">
                <h1 className="text-4xl font-bold" data-cms-field="hero.headline">
                    {fallbackHeader || pageId}
                </h1>
                <p className="mt-4 text-slate-600" data-cms-field="hero.subheadline">
                    KVSA Admin / 사이트 콘텐츠 페이지에서 "{fallbackHeader || pageId}"를 선택해 콘텐츠를 입력하세요.
                </p>
            </section>
        )
    }
    return <PageView content={content} />
}

export default function App() {
    // Header / Footer always use the 'home' content so the nav + footer
    // stay consistent across all pages.
    const { content: homeContent, source } = useSiteContent('home')
    useCmsEditMode()
    return (
        <div className="min-h-screen flex flex-col">
            <Header content={homeContent} />
            <main className="flex-1">
                <Routes>
                    <Route path="/" element={<CmsPage pageId="home" fallbackHeader="Home" />} />
                    <Route path="/about" element={<CmsPage pageId="about" fallbackHeader="About" />} />
                    <Route path="/membership" element={<CmsPage pageId="membership" fallbackHeader="Membership" />} />
                    <Route path="/news" element={<CmsPage pageId="news" fallbackHeader="News" />} />
                    <Route path="/contact" element={<CmsPage pageId="contact" fallbackHeader="Contact" />} />
                    {/* Other nav entries fall through to this catch-all. */}
                    <Route path="*" element={
                        <section className="container-fluid py-24">
                            <h1 className="text-4xl font-bold">404 - Not Found</h1>
                            <p className="mt-4 text-slate-600">
                                요청하신 페이지를 찾을 수 없습니다.
                            </p>
                        </section>
                    } />
                </Routes>
            </main>
            <Footer content={homeContent} />
            {source === 'bundled' && import.meta.env.DEV && (
                <div className="fixed bottom-4 right-4 px-3 py-2 bg-amber-100 border border-amber-300 text-amber-800 text-xs rounded shadow">
                    content source: bundled (CMS endpoint 미설정)
                </div>
            )}
        </div>
    )
}
